import {
  getReadonlyListItemDate,
  getReadonlyListItemId,
  getReadonlyListItemPill,
  getReadonlyListItemTranslatedString,
  getReadonlyListItemYesOrNo,
} from '@interxfi/app-components/components/readonly-list/util';
import type { ReadonlyListItemRecord } from '@interxfi/design-system/components/list/readonly/item/readonly-list-item';
import type { IxcRaglV1TransactionAsset, IxcRaglV1TransactionData, IxcRaglV1TransactionState } from '@interxfi/types/generated/api';
import { getAssetStateListItems } from '../../asset/asset-list-items';
import { getJ5StateMetadataListItems } from '../../j5/state/j5-state-list-items';
import { getTransactionDetailListItems } from '../../transaction-shared/transaction-list-items';
import { getRaglDeltaListItems, getRaglPortfolioLinkListItems, getRaglReverseLinkListItems } from '../shared-ragl-list-items';
import { getRaglTransactionDetailPath } from './ragl-transaction-routing';
import { RaglTransactionStatusPill } from './ragl-transaction-status-pill';

export function getRaglTransactionAssetListItems(data: IxcRaglV1TransactionAsset | undefined, prefix = '') {
  return {
    asset: data?.asset ? getAssetStateListItems(data.asset) : undefined,
    isHomeCurrency: getReadonlyListItemYesOrNo(`${prefix}isHomeCurrency`, 'Is Home Currency', Boolean(data?.isHomeCurrency)),
    deltas: data?.deltas?.map((delta, index) => getRaglDeltaListItems(delta, data.asset, `${prefix}deltas.${index}.`)),
  } as const satisfies ReadonlyListItemRecord;
}

export function getRaglTransactionDataListItems(data: IxcRaglV1TransactionData | undefined, prefix = '') {
  return {
    effectiveAt: getReadonlyListItemDate(`${prefix}effectiveAt`, 'Effective At', data?.effectiveAt, 'timestamp_iso_ms'),
    detail: getTransactionDetailListItems(data?.detail, 'ragl', `${prefix}detail.`),
    reverses: data?.reverses ? getRaglReverseLinkListItems(data.reverses, `${prefix}reverses`) : undefined,
    reversedBy: data?.reversedBy ? getRaglReverseLinkListItems(data.reversedBy, `${prefix}reversedBy`) : undefined,
    portfolio: data?.portfolio ? getRaglPortfolioLinkListItems(data.portfolio, `${prefix}portfolio.`) : undefined,
    settledByTransactionId: data?.settledByTransactionId
      ? getReadonlyListItemId(
          `${prefix}settledByTransactionId`,
          'Settled by Transaction ID',
          data.settledByTransactionId,
          getRaglTransactionDetailPath(data.settledByTransactionId),
        )
      : undefined,
    // Likely render the delta table in most cases instead of using the list items directly
    assets: data?.assets?.map((asset, index) => getRaglTransactionAssetListItems(asset, `${prefix}assets.${index}.`)),
    // TODO: contraDetail?
  } as const satisfies ReadonlyListItemRecord;
}

export function getRaglTransactionStateListItems(data: IxcRaglV1TransactionState | undefined) {
  return {
    metadata: getJ5StateMetadataListItems(data?.metadata),
    transactionId: getReadonlyListItemId('transactionId', 'Transaction ID', data?.transactionId),
    status: getReadonlyListItemTranslatedString('status', 'Status', {
      namespace: 'ixcRaglV1',
      enumName: 'IxcRaglV1TransactionStatus',
      value: data?.status,
    }),
    statusPill: getReadonlyListItemPill('statusPill', 'Status', data?.status ? <RaglTransactionStatusPill status={data.status} /> : null),
    data: getRaglTransactionDataListItems(data?.data, 'data.'),
  } as const satisfies ReadonlyListItemRecord;
}
